import dayjs from "lib/dayjs";
import { priceFromYield } from "lib/bondMath";
import { buildMunicipalCashFlows, solveNonCallableOas } from "./municipalBond";
import { priceHullWhiteBond } from "./hullWhiteCallable";

export function calibrateMunicipalCurveBasis({
  curve,
  callableYieldByTenor,
  settlementDate,
  couponRate = 5,
  maxTenorYears = 10,
  frequency = "semiannual",
  dayCount = "US MUNI: 30/360",
}) {
  const observations = [];
  for (let years = 1; years <= maxTenorYears; years += 1) {
    const yieldPct = Number(callableYieldByTenor[`${years}y`]);
    if (!Number.isFinite(yieldPct)) continue;
    const maturityDate = dayjs(settlementDate).add(years, "year").format("YYYY-MM-DD");
    const cashFlows = buildMunicipalCashFlows({ settlementDate, maturityDate, couponRate, frequency, dayCount });
    const targetPrice = priceFromYield({
      couponRate: couponRate / 100,
      yieldRate: yieldPct / 100,
      deliveryDate: settlementDate,
      maturityDate,
      frequency,
      dayCount,
    });
    const oas = solveNonCallableOas({
      cleanPrice: targetPrice,
      accruedInterest: cashFlows.accruedInterest,
      cashFlows: cashFlows.cashFlows,
      discountFactor: curve.discountFactor,
    });
    observations.push({ years, yieldPct, targetPrice, oas, oasBps: oas * 10000 });
  }
  if (!observations.length) throw new Error("No short-end callable curve points are available for basis calibration");
  const spread = observations.reduce((sum, observation) => sum + observation.oas, 0) / observations.length;
  return { spread, spreadBps: spread * 10000, observations };
}

export function calibrateEffectiveHullWhiteVolatility({
  curve,
  settlementDate,
  maturityDate,
  callableYieldPct,
  curveBasis = 0,
  meanReversion = 0.03,
  firstCallYears = 10,
  callPrice = 100,
  noticeDays = 30,
  stepsPerYear = 12,
  frequency = "semiannual",
  dayCount = "US MUNI: 30/360",
  lowerVolatilityBps = 0,
  upperVolatilityBps = 200,
}) {
  const firstCallDate = dayjs(settlementDate).add(firstCallYears, "year").format("YYYY-MM-DD");
  if (!dayjs(maturityDate).isAfter(firstCallDate, "day")) {
    return { volatilityBps: 0, targetPrice: null, modelPrice: null, priceError: 0, bracketed: true, firstCallDate };
  }
  const targetPrice = priceFromYield({
    couponRate: 0.05,
    yieldRate: Number(callableYieldPct) / 100,
    deliveryDate: settlementDate,
    maturityDate: firstCallDate,
    redemption: callPrice,
    frequency,
    dayCount,
  });
  const price = (volatilityBps) => priceHullWhiteBond({
    curve,
    settlementDate,
    maturityDate,
    couponRate: 5,
    frequency,
    dayCount,
    firstCallDate,
    callPrice,
    noticeDays,
    meanReversion,
    volatility: volatilityBps / 10000,
    stepsPerYear,
    spread: curveBasis,
  }).cleanPrice;
  let low = lowerVolatilityBps;
  let high = upperVolatilityBps;
  let lowPrice = price(low);
  let highPrice = price(high);
  if (targetPrice >= lowPrice) {
    return { volatilityBps: low, targetPrice, modelPrice: lowPrice, priceError: lowPrice - targetPrice, bracketed: false, firstCallDate };
  }
  if (targetPrice <= highPrice) {
    return { volatilityBps: high, targetPrice, modelPrice: highPrice, priceError: highPrice - targetPrice, bracketed: false, firstCallDate };
  }
  for (let iteration = 0; iteration < 18; iteration += 1) {
    const mid = (low + high) / 2;
    const midPrice = price(mid);
    if (midPrice > targetPrice) {
      low = mid;
      lowPrice = midPrice;
    } else {
      high = mid;
      highPrice = midPrice;
    }
  }
  const volatilityBps = (low + high) / 2;
  const modelPrice = (lowPrice + highPrice) / 2;
  return { volatilityBps, targetPrice, modelPrice, priceError: modelPrice - targetPrice, bracketed: true, firstCallDate };
}
